import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { encryptData } from "@excalidraw/excalidraw/data/encryption";
import { newElementWith } from "@excalidraw/element";
import throttle from "lodash.throttle";

import type { UserIdleState } from "@excalidraw/common";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import type {
  OnUserFollowedPayload,
  SocketId,
} from "@excalidraw/excalidraw/types";

import {
  COLLAB_FILE_BUFFERED_AMOUNT_LIMIT_BYTES,
  COLLAB_FILE_BUFFER_POLL_INTERVAL_MS,
  COLLAB_FILE_BUFFER_WAIT_TIMEOUT_MS,
  FILE_UPLOAD_TIMEOUT,
  WS_SUBTYPES,
} from "../app_constants";
import { isSyncableElement } from "../data";

import {
  encodeRelayPayload,
  ROOM_CONTROL_CODES,
  USER_FOLLOW_RELAY_KIND,
} from "./roomProtocol";

import type { TCollabClass } from "./Collab";
import type { RoomTransport } from "./RoomTransport";
import type {
  CollabFileMessageV1,
  RelayDurabilityV1,
  UserFollowRelayMessageV1,
} from "./roomProtocol";
import type {
  SocketUpdateData,
  SocketUpdateDataSource,
  SyncableExcalidrawElement,
} from "../data";

class Portal {
  collab: TCollabClass;
  transport: RoomTransport | null = null;
  socketInitialized: boolean = false; // we don't want the transport to emit any updates until it is fully initialized
  roomId: string | null = null;
  roomKey: string | null = null;
  broadcastedElementVersions: Map<string, number> = new Map();
  private fileSendTail: Promise<void> = Promise.resolve();
  private fileSendGeneration = 0;

  constructor(collab: TCollabClass) {
    this.collab = collab;
  }

  /**
   * Wires up the transport's room-membership controls and stores it for the
   * rest of this class to send through. Mirrors the old `socket.on(...)`
   * registrations this method used to make directly on the socket.io object:
   * `peer_joined` (was `new-user`) hands the newcomer a full scene, and
   * `member_list` (was `room-user-change`) updates the collaborator list.
   */
  open(transport: RoomTransport, id: string, key: string) {
    this.transport = transport;
    this.roomId = id;
    this.roomKey = key;

    transport.on("open", () => trackEvent("share", "room joined"));

    transport.on("control", (control) => {
      switch (control.code) {
        case ROOM_CONTROL_CODES.PEER_JOINED:
          this.broadcastScene(
            WS_SUBTYPES.INIT,
            this.collab.getSceneElementsIncludingDeleted(),
            /* syncAll */ true,
          );
          break;
        case ROOM_CONTROL_CODES.MEMBER_LIST:
          this.collab.setCollaborators(
            control.members.map((clientId) => clientId as SocketId),
          );
          break;
        default:
          // PEER_LEFT and ROOM_CLOSING are handled directly by Collab.
          break;
      }
    });

    return transport;
  }

  close() {
    if (!this.transport) {
      return;
    }
    this.queueFileUpload.flush();
    this.transport.close();
    this.transport = null;
    this.roomId = null;
    this.roomKey = null;
    this.socketInitialized = false;
    this.broadcastedElementVersions = new Map();
    this.fileSendGeneration += 1;
    this.fileSendTail = Promise.resolve();
  }

  isOpen() {
    return !!(
      this.socketInitialized &&
      this.transport?.isConnected &&
      this.roomId &&
      this.roomKey
    );
  }

  /**
   * Encrypts one JSON-serializable payload with the room key and sends it as
   * a relay envelope. Shared by scene/pointer broadcasts (`SocketUpdateData`)
   * and the app-only follow message, since both need the exact same
   * encrypt-then-send steps and the room relay never inspects `payload`
   * itself. Returns whether the transport actually accepted the send, so a
   * caller can tell a delivered payload from a dropped one.
   */
  private async _sendEncryptedRelay(
    payload: SocketUpdateData | UserFollowRelayMessageV1 | CollabFileMessageV1,
    durability: RelayDurabilityV1,
  ): Promise<boolean> {
    if (!this.isOpen()) {
      return false;
    }
    const json = JSON.stringify(payload);
    const encoded = new TextEncoder().encode(json);
    const { encryptedBuffer, iv } = await encryptData(this.roomKey!, encoded);
    const encodedPayload = encodeRelayPayload(iv, encryptedBuffer);
    return this.transport?.sendRelay(encodedPayload, durability) ?? false;
  }

  /** Waits for the browser send buffer to drain before another large file frame. */
  private async waitForFileBufferAvailability(
    generation: number,
  ): Promise<boolean> {
    const deadline = Date.now() + COLLAB_FILE_BUFFER_WAIT_TIMEOUT_MS;
    while (
      generation === this.fileSendGeneration &&
      this.isOpen() &&
      (this.transport?.bufferedAmount ?? 0) >
        COLLAB_FILE_BUFFERED_AMOUNT_LIMIT_BYTES
    ) {
      if (Date.now() >= deadline) {
        console.error("Collaboration file send buffer did not drain in time");
        return false;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, COLLAB_FILE_BUFFER_POLL_INTERVAL_MS);
      });
    }
    return generation === this.fileSendGeneration && this.isOpen();
  }

  /** Serializes durable file frames without blocking scene or cursor messages. */
  broadcastFileMessage = (message: CollabFileMessageV1): Promise<boolean> => {
    const generation = this.fileSendGeneration;
    const task = this.fileSendTail.then(async () => {
      if (!(await this.waitForFileBufferAvailability(generation))) {
        return false;
      }
      try {
        return await this._sendEncryptedRelay(message, "durable");
      } catch (error) {
        console.error("Failed to encrypt a collaboration file message", error);
        return false;
      }
    });
    this.fileSendTail = task.then(() => undefined);
    return task;
  };

  /** Returns whether the transport accepted the send, per `_sendEncryptedRelay`. */
  async _broadcastSocketData(
    data: SocketUpdateData,
    volatile: boolean = false,
  ): Promise<boolean> {
    return this._sendEncryptedRelay(data, volatile ? "volatile" : "durable");
  }

  queueFileUpload = throttle(async () => {
    try {
      await this.collab.fileManager.saveFiles({
        elements: this.collab.excalidrawAPI.getSceneElementsIncludingDeleted(),
        files: this.collab.excalidrawAPI.getFiles(),
      });
    } catch (error: any) {
      if (error.name !== "AbortError") {
        this.collab.excalidrawAPI.updateScene({
          appState: {
            errorMessage: error.message,
          },
        });
      }
    }

    let isChanged = false;
    const newElements = this.collab.excalidrawAPI
      .getSceneElementsIncludingDeleted()
      .map((element) => {
        if (this.collab.fileManager.shouldUpdateImageElementStatus(element)) {
          isChanged = true;
          // this will signal collaborators to pull image data from server
          // (using mutation instead of newElementWith otherwise it'd break
          // in-progress dragging)
          return newElementWith(element, { status: "saved" });
        }
        return element;
      });

    if (isChanged) {
      this.collab.excalidrawAPI.updateScene({
        elements: newElements,
        captureUpdate: CaptureUpdateAction.NEVER,
      });
    }
  }, FILE_UPLOAD_TIMEOUT);

  broadcastScene = async (
    updateType: WS_SUBTYPES.INIT | WS_SUBTYPES.UPDATE,
    elements: readonly OrderedExcalidrawElement[],
    syncAll: boolean,
  ) => {
    if (updateType === WS_SUBTYPES.INIT && !syncAll) {
      throw new Error("syncAll must be true when sending SCENE.INIT");
    }

    // sync out only the elements we think we need to to save bandwidth.
    // periodically we'll resync the whole thing to make sure no one diverges
    // due to a dropped message (server goes down etc).
    const syncableElements = elements.reduce((acc, element) => {
      if (
        (syncAll ||
          !this.broadcastedElementVersions.has(element.id) ||
          element.version > this.broadcastedElementVersions.get(element.id)!) &&
        isSyncableElement(element)
      ) {
        acc.push(element);
      }
      return acc;
    }, [] as SyncableExcalidrawElement[]);

    const data: SocketUpdateDataSource[typeof updateType] = {
      type: updateType,
      payload: {
        elements: syncableElements,
      },
    };

    this.queueFileUpload();

    // Only record these elements as broadcast once the send is actually
    // accepted. Marking them beforehand (the old behavior) meant a failed
    // send was remembered as delivered and never retried, so a peer would
    // silently miss it until the next full-scene resync papered over it.
    const accepted = await this._broadcastSocketData(data as SocketUpdateData);
    if (accepted) {
      for (const syncableElement of syncableElements) {
        this.broadcastedElementVersions.set(
          syncableElement.id,
          syncableElement.version,
        );
      }
    }
  };

  broadcastIdleChange = (userState: UserIdleState) => {
    const clientId = this.transport?.clientId;
    if (clientId) {
      const data: SocketUpdateDataSource["IDLE_STATUS"] = {
        type: WS_SUBTYPES.IDLE_STATUS,
        payload: {
          socketId: clientId as SocketId,
          userState,
          username: this.collab.state.username,
        },
      };
      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastMouseLocation = (payload: {
    pointer: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["pointer"];
    button: SocketUpdateDataSource["MOUSE_LOCATION"]["payload"]["button"];
  }) => {
    const clientId = this.transport?.clientId;
    if (clientId) {
      const data: SocketUpdateDataSource["MOUSE_LOCATION"] = {
        type: WS_SUBTYPES.MOUSE_LOCATION,
        payload: {
          socketId: clientId as SocketId,
          pointer: payload.pointer,
          button: payload.button || "up",
          selectedElementIds:
            this.collab.excalidrawAPI.getAppState().selectedElementIds,
          username: this.collab.state.username,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  broadcastVisibleSceneBounds = (payload: {
    sceneBounds: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"]["payload"]["sceneBounds"];
  }) => {
    const clientId = this.transport?.clientId;
    if (clientId) {
      const data: SocketUpdateDataSource["USER_VISIBLE_SCENE_BOUNDS"] = {
        type: WS_SUBTYPES.USER_VISIBLE_SCENE_BOUNDS,
        payload: {
          socketId: clientId as SocketId,
          username: this.collab.state.username,
          sceneBounds: payload.sceneBounds,
        },
      };

      return this._broadcastSocketData(
        data as SocketUpdateData,
        true, // volatile
      );
    }
  };

  /**
   * Sends a follow/unfollow signal to every peer. The room relay has no
   * concept of "follow" (it only relays opaque encrypted payloads), so this
   * rides the same encrypted channel as scene/pointer data instead of the
   * old plaintext socket.io event. See `roomProtocol.ts` for why.
   */
  broadcastUserFollowed = (payload: OnUserFollowedPayload) => {
    if (this.transport?.clientId) {
      const message: UserFollowRelayMessageV1 = {
        kind: USER_FOLLOW_RELAY_KIND,
        action: payload.action,
        targetClientId: payload.userToFollow.socketId,
      };
      return this._sendEncryptedRelay(message, "durable");
    }
  };
}

export default Portal;
